import { getOrganizationScopes, IScope } from "@/api";
import { InsertDriveFileOutlined } from "@mui/icons-material";
import {
    alpha,
    Box,
    Checkbox,
    CircularProgress,
    Typography,
} from "@mui/material";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useInView } from "react-intersection-observer";

const PAGE_SIZE = 10;
export const MAX_SCOPES = 5;

interface ScopeSelectionListProps {
    clauseChecklistId: number;
    organizationId: number;
    initialScopes: Pick<IScope, "id" | "document_title">[];
}

export const ScopeSelectionList = ({
    clauseChecklistId,
    organizationId,
    initialScopes,
}: ScopeSelectionListProps) => {
    const { watch, getValues, setValue } = useFormContext();
    const scopeIds: number[] = watch("scope_ids");
    const { ref, inView } = useInView();

    const {
        data: scopesData,
        isPending,
        isFetchingNextPage,
        isFetching,
        hasNextPage,
        fetchNextPage,
    } = useInfiniteQuery({
        queryKey: ["frontend-scopes-checkbox-group", clauseChecklistId],
        queryFn: (ctx) =>
            getOrganizationScopes(organizationId, ctx.pageParam, PAGE_SIZE, {
                q: clauseChecklistId.toString(),
                sort_by: "clause_checklist_id",
            }),
        getNextPageParam: (lastGroup) => {
            if (lastGroup.items.length < PAGE_SIZE) return undefined;
            return lastGroup.page + 1;
        },
        initialPageParam: 1,
    });

    const combinedScopes = useMemo(() => {
        const seen = new Set<number>();
        const result: Pick<IScope, "id" | "document_title">[] = [];

        for (const scope of initialScopes) {
            if (!seen.has(scope.id)) {
                seen.add(scope.id);
                result.push(scope);
            }
        }

        for (const page of scopesData?.pages ?? []) {
            for (const scope of page.items) {
                if (!seen.has(scope.id)) {
                    seen.add(scope.id);
                    result.push({
                        id: scope.id,
                        document_title: scope.document_title,
                    });
                }
            }
        }

        return result;
    }, [initialScopes, scopesData?.pages]);

    useEffect(() => {
        if (inView && hasNextPage && !isFetching) {
            fetchNextPage();
        }
    }, [fetchNextPage, inView, hasNextPage, isFetching]);

    const handleToggle = (scopeId: number) => {
        const current: number[] = getValues("scope_ids");
        if (current.includes(scopeId)) {
            setValue(
                "scope_ids",
                current.filter((id) => id !== scopeId)
            );
        } else if (current.length < MAX_SCOPES) {
            setValue("scope_ids", [...current, scopeId]);
        }
    };

    const atLimit = scopeIds.length >= MAX_SCOPES;

    return (
        <Box
            sx={{
                width: "100%",
                border: 1,
                borderColor: "divider",
                borderRadius: 1.5,
                overflow: "hidden",
            }}
        >
            {/* Scrollable list */}
            <Box
                sx={(theme) => ({
                    overflowY: "auto",
                    overflowX: "hidden",
                    maxHeight: { xs: 200, sm: 280 },
                    "&::-webkit-scrollbar": { width: 6 },
                    "&::-webkit-scrollbar-track": {
                        background: "transparent",
                    },
                    "&::-webkit-scrollbar-thumb": {
                        borderRadius: 3,
                        backgroundColor: theme.palette.grey[300],
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                        backgroundColor: theme.palette.grey[400],
                    },
                    scrollbarWidth: "thin",
                    scrollbarColor: `${theme.palette.grey[300]} transparent`,
                })}
            >
                {combinedScopes.map((scope) => {
                    const isSelected = scopeIds.includes(scope.id);
                    const isDisabled = atLimit && !isSelected;
                    return (
                        <Box
                            key={scope.id}
                            onClick={() =>
                                !isDisabled && handleToggle(scope.id)
                            }
                            sx={(theme) => ({
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                px: 1,
                                py: 0.875,
                                cursor: isDisabled ? "default" : "pointer",
                                userSelect: "none",
                                transition: "all 0.15s ease",
                                opacity: isDisabled ? 0.45 : 1,
                                borderLeft: "3px solid",
                                borderLeftColor: isSelected
                                    ? theme.palette.primary.main
                                    : "transparent",
                                bgcolor: isSelected
                                    ? alpha(theme.palette.primary.main, 0.04)
                                    : "transparent",
                                "&:hover": isDisabled
                                    ? {}
                                    : {
                                          bgcolor: isSelected
                                              ? alpha(
                                                    theme.palette.primary.main,
                                                    0.08
                                                )
                                              : theme.palette.grey[50],
                                      },
                                "&:not(:last-child)": {
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                },
                            })}
                        >
                            <Checkbox
                                size="small"
                                checked={isSelected}
                                disabled={isDisabled}
                                onChange={() => handleToggle(scope.id)}
                                onClick={(e) => e.stopPropagation()}
                                sx={{
                                    p: 0.5,
                                    "&.Mui-checked": {
                                        color: "primary.main",
                                    },
                                }}
                            />
                            <InsertDriveFileOutlined
                                sx={{
                                    fontSize: 18,
                                    color: isSelected
                                        ? "primary.main"
                                        : "text.disabled",
                                    flexShrink: 0,
                                    transition: "color 0.15s ease",
                                }}
                            />
                            <Typography
                                variant="body2"
                                noWrap
                                sx={{
                                    fontWeight: isSelected ? 600 : 400,
                                    color: isSelected
                                        ? "text.primary"
                                        : "text.secondary",
                                    transition: "all 0.15s ease",
                                    minWidth: 0,
                                    fontSize: "0.8125rem",
                                }}
                            >
                                {scope.document_title}
                            </Typography>
                        </Box>
                    );
                })}

                {isPending && (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            py: 3,
                        }}
                    >
                        <CircularProgress size={20} />
                    </Box>
                )}

                {hasNextPage && !isFetching && <div ref={ref} />}

                {isFetchingNextPage && (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            py: 1.5,
                        }}
                    >
                        <CircularProgress size={16} />
                    </Box>
                )}

                {!isPending && combinedScopes.length === 0 && (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            py: 3,
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            No scopes found for this checklist.
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};
